/** Actions ▾ — éditer, reçu, mail, remboursement. */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ActionChevron } from "../../components/ActionChevron";
import { Button } from "../../components/Button";
import { placeFixedMenu, watchMenuPlace } from "../../components/placeMenu";
import { IcoEdit, IcoMail, IcoPay, IcoPrint } from "../quotes/QuoteMenuIcons";
import "../quotes/quotes.css";

const MENU_W = 260;

type PaymentMenuProps = {
  busy?: boolean;
  canRefund?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEdit?: () => void;
  onPrint?: () => void;
  onSend?: () => void;
  onRefund?: () => void;
};

export function PaymentMenu({ busy, canRefund, open: openProp, onOpenChange, onEdit, onPrint, onSend, onRefund }: PaymentMenuProps) {
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [inner, setInner] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 320 });
  const open = openProp ?? inner;

  function setOpen(next: boolean) {
    if (onOpenChange) onOpenChange(next);
    if (openProp === undefined) setInner(next);
  }

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = box.current?.querySelector("button")?.getBoundingClientRect();
      if (!r) return;
      setPos(placeFixedMenu(r, MENU_W, menu.current?.scrollHeight || 240));
    };
    const stop = watchMenuPlace(place);
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || menu.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      stop();
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  function row(fn: (() => void) | undefined, ico: ReactNode, text: string) {
    if (!fn) return null;
    return (
      <button
        type="button"
        className="fx-quote-menu__item"
        role="menuitem"
        onClick={() => {
          setOpen(false);
          fn();
        }}
      >
        {ico} {text}
      </button>
    );
  }

  return (
    <div className="fx-quote-menu" ref={box}>
      <Button className="fx-chev-btn" disabled={busy} aria-expanded={open} onClick={() => setOpen(!open)}>
        Actions <ActionChevron />
      </Button>
      {open
        ? createPortal(
            <div
              ref={menu}
              className="fx-quote-menu__list"
              role="menu"
              style={{ top: pos.top, left: pos.left, width: MENU_W, maxHeight: pos.maxHeight, overflowY: "auto" }}
            >
              {row(onEdit, <IcoEdit />, "Éditer")}
              {row(onPrint, <IcoPrint />, "Imprimer le reçu")}
              {row(onSend, <IcoMail />, "Envoyer le reçu")}
              {canRefund ? row(onRefund, <IcoPay />, "Rembourser") : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
